import React, { useState, useEffect } from "react";
import { getFeaturedRotation, setFeaturedRotation } from "../../api";
import { RefreshCw, Loader2, Plus, X, Radio } from "lucide-react";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

const AdminFeaturedRotation = ({ developments = [] }) => {
  const { toast } = useToast();
  const [rot, setRot] = useState({ enabled: false, intervalDays: 7, queue: [], current: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRot(await getFeaturedRotation()); } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const r = await setFeaturedRotation({
        enabled: next.enabled, intervalDays: next.intervalDays,
        devIds: (next.queue || []).map((q) => q.id),
      });
      setRot(r);
    } catch { toast({ title: "Couldn't save rotation", variant: "destructive" }); }
    setSaving(false);
  };

  const toggle = (v) => persist({ ...rot, enabled: v });
  const setInterval = (d) => persist({ ...rot, intervalDays: d });
  const addDev = (id) => {
    if (!id || rot.queue.some((q) => q.id === id)) return;
    const dev = developments.find((d) => d.id === id);
    persist({ ...rot, queue: [...rot.queue, { id, name: dev?.name || "—" }] });
  };
  const removeDev = (id) => persist({ ...rot, queue: rot.queue.filter((q) => q.id !== id) });

  const available = developments.filter((d) => !rot.queue.some((q) => q.id === d.id));

  return (
    <div className="bg-white border border-[#f0ece5] rounded-xl p-4 mb-5" data-testid="featured-rotation">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-[14px] font-semibold text-[#2b2b2b] flex items-center gap-2"><RefreshCw size={16} className="text-[#b8935f]" /> Featured rotation</h3>
          <p className="text-[12px] text-[#8a8a8a] mt-0.5">Queue several developments to auto-rotate as the homepage feature. Overrides the manual star while on.</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <Loader2 size={14} className="animate-spin text-[#8a8a8a]" />}
          <label className="text-[12px] text-[#6a6a6a] flex items-center gap-2">Every
            <select value={rot.intervalDays} onChange={(e) => setInterval(+e.target.value)} data-testid="rotation-interval" className="text-[13px] border border-[#f0ece5] rounded-lg px-2 py-1.5">
              <option value={1}>day</option><option value={7}>week</option><option value={14}>2 weeks</option><option value={30}>month</option>
            </select>
          </label>
          <Switch checked={rot.enabled} onCheckedChange={toggle} data-testid="rotation-toggle" />
        </div>
      </div>

      {loading ? <div className="py-4"><Loader2 size={16} className="animate-spin text-[#8a8a8a]" /></div> : (
        <div className="mt-3 space-y-2">
          {rot.queue.length === 0 ? (
            <div className="text-[13px] text-[#8a8a8a]">No developments queued yet — add some below.</div>
          ) : rot.queue.map((q) => (
            <div key={q.id} className={`flex items-center gap-2 text-[13px] rounded-lg px-3 py-2 ${rot.current === q.id && rot.enabled ? "bg-[#faf3e9] border border-[#e7d9c3]" : "bg-[#faf7f2]"}`} data-testid={`rotation-item-${q.id}`}>
              {rot.current === q.id && rot.enabled && <span className="text-[10px] text-[#b8935f] flex items-center gap-1 font-medium"><Radio size={11} /> LIVE</span>}
              <span className="flex-1 text-[#2b2b2b]">{q.name}</span>
              <button onClick={() => removeDev(q.id)} data-testid={`rotation-remove-${q.id}`} className="text-[#a8a8a8] hover:text-red-500"><X size={15} /></button>
            </div>
          ))}
          {available.length > 0 && (
            <select value="" onChange={(e) => addDev(e.target.value)} data-testid="rotation-add" className="text-[13px] border border-[#f0ece5] rounded-lg px-3 py-2 w-full text-[#6a6a6a]">
              <option value="">+ Add a development to the rotation…</option>
              {available.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminFeaturedRotation;
